import { ArrowRight, Flag, ShieldAlert, TriangleAlert } from 'lucide-react';

/**
 * Trading-safety card on the chat home screen.
 *
 * Policy wording is deliberately strict: off-site trading is prohibited, not
 * merely discouraged. The consequence and "report it" notices reuse the amber
 * warning palette already used by SafetyBanner, so both stay inside the
 * contrast-audited token set (see scripts/check-contrast.mjs).
 *
 * The safety-guidelines link is absolute: the real page lives on the main site
 * (web/safety-guidelines.html, canonical https://z2market.com/safety-guidelines)
 * and this app is on a different subdomain, so a relative path would 404.
 */
const PROHIBITED_ACTIONS = [
  'Do not share or request off-site contact information.',
  'Do not move any transaction or payment outside Z2Market.',
  'Do not conduct private deals with buyers or sellers.',
  'Do not accept or request payments outside Z2Market.',
  "Do not bypass Z2Market's official order and payment system.",
];

export default function ReportGuidelinesCard() {
  return (
    <section
      aria-labelledby="z2-report-heading"
      className="w-full rounded-2xl border border-line bg-surface p-5 text-left shadow-soft"
    >
      <header className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-soft text-accent">
          <ShieldAlert size={17} />
        </span>
        <h2
          id="z2-report-heading"
          className="pt-1 text-[13.5px] leading-snug font-semibold text-ink"
        >
          Off-Site Trading Is Strictly Prohibited
        </h2>
      </header>

      <p className="mt-3 text-[12.5px] leading-relaxed text-ink-2">
        Keep all communication, payments, and transactions on Z2Market. Moving deals or
        payments outside Z2Market is strictly prohibited.
      </p>

      <ul className="mt-3.5 space-y-2.5 pl-1">
        {PROHIBITED_ACTIONS.map((action) => (
          <li key={action} className="flex items-start gap-2.5">
            <span
              aria-hidden="true"
              className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600"
            />
            <span className="text-[12.5px] leading-relaxed text-ink-2">{action}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 dark:border-amber-500/25 dark:bg-amber-500/10">
        <TriangleAlert
          size={15}
          aria-hidden="true"
          className="mt-px shrink-0 text-amber-700 dark:text-amber-300"
        />
        <p className="text-[12.5px] leading-relaxed text-amber-900 dark:text-amber-100/90">
          Violations may result in immediate account suspension or permanent account
          termination. Funds may be withheld or become unavailable depending on the
          circumstances and applicable policies. Serious or suspicious cases may be handled
          differently based on the circumstances.
        </p>
      </div>

      <p className="mt-3 flex items-start gap-2.5 rounded-xl border border-line bg-surface-2 px-3.5 py-3 text-[12.5px] leading-relaxed font-medium text-ink">
        <Flag size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-accent" />
        If someone asks you to trade or pay outside Z2Market, report them immediately.
      </p>

      <a
        href="https://z2market.com/safety-guidelines"
        className="z2-glow-hover mt-4 inline-flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-[12.5px] font-medium text-accent transition hover:border-brand-300 hover:bg-brand-soft"
      >
        Read more about Trading Safety Guidelines
        <ArrowRight size={14} />
      </a>
    </section>
  );
}
